import { z } from 'zod';
import { IsoDateTime, Uuid } from './common';

export const FileKind = z.enum([
  'sliced',
  'thumbnail',
  'reference',
  'other',
  'model',
  'source',
  'document',
  'media',
  'derived',
]);
export type FileKind = z.infer<typeof FileKind>;

/** Kinds a client may upload (`thumbnail` and `derived` are made by the server). */
export const UploadKind = z.enum(['sliced', 'reference', 'model', 'source', 'document', 'media']);
export type UploadKind = z.infer<typeof UploadKind>;

const MB = 1024 * 1024;
const IMAGES = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];

/** Upload limits per kind (references "até 25 MB"; sliced projects and videos can be large). */
export const FILE_LIMITS: Record<UploadKind, { maxBytes: number; extensions: string[] }> = {
  sliced: { maxBytes: 200 * MB, extensions: ['.3mf', '.gcode', '.stl'] },
  reference: {
    maxBytes: 25 * MB,
    extensions: [...IMAGES, '.gif', '.pdf', '.3mf', '.stl', '.step', '.stp', '.txt'],
  },
  model: { maxBytes: 500 * MB, extensions: ['.stl', '.3mf', '.obj', '.step', '.stp'] },
  source: {
    maxBytes: 500 * MB,
    extensions: ['.step', '.stp', '.f3d', '.f3z', '.blend', '.scad', '.fcstd', '.skp'],
  },
  document: { maxBytes: 25 * MB, extensions: ['.pdf', '.txt', '.md', ...IMAGES, '.xml', '.zip'] },
  media: { maxBytes: 4096 * MB, extensions: [...IMAGES, '.mp4', '.mov', '.m4v'] },
};

export const StoredFile = z
  .object({
    id: Uuid,
    kind: FileKind,
    name: z.string(),
    mime: z.string(),
    sizeBytes: z.number().int(),
    sha256: z.string(),
    url: z.string().describe('Download URL (`GET /api/v1/files/{id}/content`).'),
    owner: z.object({ type: z.string(), id: Uuid }).nullable(),
    duplicateOf: Uuid.nullable().describe('Another file with the same content (sha256), if any.'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'StoredFile' });
export type StoredFile = z.infer<typeof StoredFile>;

export const UploadQuery = z.object({
  kind: UploadKind.default('reference'),
});

export const StorageUsage = z
  .object({
    totalBytes: z.number().int(),
    groups: z.array(
      z.object({
        group: z.enum(['media', 'product_files', 'quotes', 'other']),
        bytes: z.number().int(),
        files: z.number().int(),
      }),
    ),
    videoBytes: z.number().int().describe('Video originals and previews.'),
    products: z
      .array(z.object({ id: Uuid, name: z.string(), bytes: z.number().int() }))
      .describe('The 10 products using the most space (files and media).'),
  })
  .meta({ id: 'StorageUsage' });
